/* InvoicePrintView — premium B2B tax invoice (Practo / Stripe / FreshBooks grade).
 *
 * Anchored on the doctor's clinic identity: logo, letterhead, clinic
 * name, address, GSTIN, DCI. Invisique is the platform — never the biller.
 *
 * iter98.99aa (2026-06) — full visual overhaul to look like a premium
 * SaaS B2B invoice. Highlights:
 *   • Brand-color accent band at the top
 *   • Two-column "From / Bill To" identity block
 *   • Status watermark stamp (PAID / VOID / OVERDUE) over the body
 *   • UPI QR code + Scan-to-pay callout (when doctor has a UPI ID)
 *   • Modern itemized table with alternating row tones
 *   • Refined totals block with CGST/SGST friendly tax label
 *   • Signature placeholder + Terms section + premium footer
 *
 * Clinic identity comes from AuthContext (`/auth/me`):
 *   doctor_clinic_logo_data_url, doctor_clinic_letterhead,
 *   doctor_clinic_name, doctor_name, doctor_clinic_phone,
 *   doctor_clinic_address, doctor_gstin, doctor_dci_registration_number,
 *   doctor_upi_id, doctor_billing_footer
 *
 * All fields are optional — the layout collapses gracefully for clinics
 * that haven't set every brand element.
 */
import React, { useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, X, QrCode } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/context/AuthContext";


function formatINR(amount) {
    if (amount === null || amount === undefined) return "—";
    return new Intl.NumberFormat("en-IN", {
        style: "currency", currency: "INR", maximumFractionDigits: 2,
    }).format(amount);
}

function fmtLongDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-IN", {
        day: "numeric", month: "long", year: "numeric",
    });
}

/* Status -> watermark stamp. Renders a faint, large, rotated text
 * stamp over the body so the invoice's life-stage reads at a glance.
 * Returns null when no stamp is appropriate (draft / sent / viewed / partial). */
function statusStamp(status, isOverdue) {
    if (isOverdue) return { text: "OVERDUE", tone: "text-rose-600/15 border-rose-600/30" };
    if (status === "paid") return { text: "PAID", tone: "text-emerald-600/15 border-emerald-600/30" };
    if (status === "refunded") return { text: "REFUNDED", tone: "text-violet-600/15 border-violet-600/30" };
    if (status === "void") return { text: "VOID", tone: "text-rose-700/20 border-rose-700/30" };
    if (status === "uncollectible") return { text: "WRITTEN OFF", tone: "text-slate-700/15 border-slate-700/30" };
    return null;
}

/* Compose the official UPI deep-link string. Standardised format that
 * every Indian payment app (PhonePe, GPay, Paytm, BHIM, …) understands:
 *   upi://pay?pa=<vpa>&pn=<name>&am=<amt>&cu=INR&tn=<note>
 */
function buildUpiUri({ upiId, payeeName, amount, invoiceCode }) {
    if (!upiId) return null;
    const params = new URLSearchParams();
    params.set("pa", upiId);
    if (payeeName) params.set("pn", payeeName);
    if (amount) params.set("am", Number(amount).toFixed(2));
    params.set("cu", "INR");
    if (invoiceCode) params.set("tn", `Invoice ${invoiceCode}`);
    return `upi://pay?${params.toString()}`;
}


export default function InvoicePrintView({ open, invoice, onClose }) {
    const { user } = useAuth();

    /* ─────────────── Clinic identity (from /auth/me) ─────────────── */
    const clinicName       = user?.doctor_clinic_name      || user?.clinic_name      || "My Dental Practice";
    const doctorName       = user?.doctor_name             || user?.name             || "";
    const clinicPhone      = user?.doctor_clinic_phone     || user?.phone            || "";
    const clinicAddress    = user?.doctor_clinic_address   || user?.address          || "";
    const clinicGstin      = user?.doctor_gstin            || user?.gstin            || "";
    const clinicLogo       = user?.doctor_clinic_logo_data_url || "";
    const clinicLetterhead = (user?.doctor_clinic_letterhead || "").trim();
    const dciNumber        = (user?.doctor_dci_registration_number || "").trim();
    const doctorUpiId      = (user?.doctor_upi_id || "").trim();
    const billingFooter    = (user?.doctor_billing_footer || "").trim() ||
                             "Thank you for placing your smile in our care.";

    /* Letterhead, when set, takes top billing on the invoice header. */
    const headerTitle = clinicLetterhead || clinicName;

    /* Initials monogram for clinics without a logo — Practo-style "AM"
       circle. Falls back to the first letter of the clinic name. */
    const monogram = useMemo(() => {
        if (clinicLogo) return null;
        const src = clinicLetterhead || clinicName || "C";
        const words = src.trim().split(/\s+/).slice(0, 2);
        return words.map((w) => w[0]?.toUpperCase()).join("") || "C";
    }, [clinicLogo, clinicLetterhead, clinicName]);

    /* ─────────────── Print-only stylesheet ─────────────── */
    useEffect(() => {
        if (!open) return;
        const style = document.createElement("style");
        style.id = "invoice-print-styles";
        style.innerHTML = `
            @media print {
                @page { size: A4; margin: 12mm; }
                body * { visibility: hidden !important; }
                [data-print-root="invoice"], [data-print-root="invoice"] * {
                    visibility: visible !important;
                }
                [data-print-root="invoice"] {
                    position: fixed !important;
                    inset: 0 !important;
                    background: white !important;
                    padding: 0 !important;
                    overflow: visible !important;
                    max-height: none !important;
                    box-shadow: none !important;
                    border: 0 !important;
                }
                .no-print { display: none !important; }
                [role="dialog"] { box-shadow: none !important; border: 0 !important; }
                [data-state="open"][role="dialog"] { transform: none !important; }
            }
        `;
        document.head.appendChild(style);
        return () => { document.getElementById("invoice-print-styles")?.remove(); };
    }, [open]);

    if (!invoice) return null;

    const isOverdue = invoice.due_at
        && (invoice.balance || 0) > 0
        && !["paid", "void", "refunded", "uncollectible"].includes(invoice.status)
        && new Date(invoice.due_at) < new Date();
    const stamp = statusStamp(invoice.status, isOverdue);

    /* Tax label — invoices in India are commonly split CGST + SGST (each
     * half the GST rate) for intra-state, or IGST for inter-state. Without
     * the patient's state on file, we display a single "GST" line that
     * the doctor can manually re-label later. */
    const taxLabel = invoice.tax_pct > 0 ? `GST (${invoice.tax_pct}%)` : "Tax";

    const upiUri = buildUpiUri({
        upiId: doctorUpiId,
        payeeName: clinicName,
        amount: invoice.balance > 0 ? invoice.balance : undefined,
        invoiceCode: invoice.code,
    });
    const showUpiQR = !!upiUri && invoice.balance > 0 &&
                      !["void", "uncollectible", "refunded"].includes(invoice.status);

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent
                className="max-w-4xl max-h-[95vh] overflow-y-auto p-0 bg-white"
                data-print-root="invoice"
                data-testid="invoice-print-view"
            >
                <DialogTitle className="sr-only">Invoice {invoice.code} — print preview</DialogTitle>

                {/* ─────────── Top toolbar (hidden in print) ─────────── */}
                <div className="no-print sticky top-0 z-10 bg-secondary/40 border-b border-border px-4 py-2.5 flex items-center justify-between gap-3 backdrop-blur">
                    <div className="text-xs text-muted-foreground">
                        Preview · use your browser's print dialog to save as PDF
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={onClose} className="h-8 rounded-full" data-testid="invoice-print-close">
                            <X size={13} weight="bold" className="mr-1" /> Close
                        </Button>
                        <Button size="sm" onClick={() => window.print()} className="h-8 rounded-full" data-testid="invoice-print-button">
                            <Printer size={13} weight="duotone" className="mr-1" /> Print / Save PDF
                        </Button>
                    </div>
                </div>

                {/* ─────────── INVOICE BODY ─────────── */}
                <div className="relative p-8 lg:p-10 font-sans text-foreground bg-white">

                    {/* Watermark stamp (status) — sits behind everything */}
                    {stamp && (
                        <div
                            aria-hidden
                            className="pointer-events-none absolute inset-0 grid place-items-center"
                            data-testid="invoice-status-stamp"
                        >
                            <span
                                className={`font-display font-black tracking-[0.2em] text-[140px] leading-none border-[10px] rounded-3xl px-10 py-4 select-none ${stamp.tone}`}
                                style={{ transform: "rotate(-18deg)" }}
                            >
                                {stamp.text}
                            </span>
                        </div>
                    )}

                    <div className="relative">
                        {/* ─────────── Brand accent band ─────────── */}
                        <div
                            className="h-1.5 rounded-full mb-7 bg-gradient-to-r from-primary via-primary to-emerald-500"
                            data-testid="invoice-brand-band"
                        />

                        {/* ─────────── HEADER ─────────── */}
                        <div className="flex items-start justify-between gap-8 mb-10">
                            {/* LEFT — clinic identity */}
                            <div className="min-w-0 flex items-start gap-4 flex-1">
                                {clinicLogo ? (
                                    <img
                                        src={clinicLogo}
                                        alt={`${headerTitle} logo`}
                                        className="h-16 w-auto max-w-[120px] object-contain shrink-0"
                                        data-testid="invoice-clinic-logo"
                                    />
                                ) : (
                                    <div
                                        className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-emerald-600 text-white grid place-items-center font-display text-2xl font-bold shrink-0"
                                        data-testid="invoice-clinic-monogram"
                                    >
                                        {monogram}
                                    </div>
                                )}
                                <div className="min-w-0">
                                    <div
                                        className="font-display text-3xl font-bold tracking-tight leading-none"
                                        data-testid="invoice-clinic-header-title"
                                    >
                                        {headerTitle}
                                    </div>
                                    {clinicLetterhead && clinicName && clinicLetterhead !== clinicName && (
                                        <div className="text-sm text-muted-foreground mt-1">{clinicName}</div>
                                    )}
                                    {doctorName && (
                                        <div className="text-sm text-foreground/85 mt-2">{doctorName}</div>
                                    )}
                                    {dciNumber && (
                                        <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">DCI Reg. No. {dciNumber}</div>
                                    )}
                                </div>
                            </div>

                            {/* RIGHT — Tax Invoice label + invoice meta */}
                            <div className="shrink-0 text-right">
                                <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-primary">
                                    Tax Invoice
                                </div>
                                <div className="font-mono text-xl font-semibold mt-1 tabular-nums">
                                    {invoice.code}
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-3 space-y-0.5">
                                    {invoice.issued_at && (
                                        <div>
                                            <span className="text-muted-foreground/80">Issued · </span>
                                            <span className="text-foreground font-medium">{fmtLongDate(invoice.issued_at)}</span>
                                        </div>
                                    )}
                                    {invoice.due_at && (
                                        <div>
                                            <span className="text-muted-foreground/80">Due · </span>
                                            <span className={`font-medium ${isOverdue ? "text-rose-700" : "text-foreground"}`}>
                                                {fmtLongDate(invoice.due_at)}
                                            </span>
                                        </div>
                                    )}
                                    {invoice.paid_at && (
                                        <div>
                                            <span className="text-muted-foreground/80">Paid · </span>
                                            <span className="text-emerald-700 font-medium">{fmtLongDate(invoice.paid_at)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ─────────── FROM / BILL TO ─────────── */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
                            <BlockCard label="From" testid="invoice-from-block">
                                <div className="text-sm font-semibold text-foreground">{clinicName}</div>
                                {clinicAddress && (
                                    <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap leading-relaxed">
                                        {clinicAddress}
                                    </div>
                                )}
                                {clinicPhone && (
                                    <div className="text-xs text-muted-foreground mt-1">{clinicPhone}</div>
                                )}
                                {clinicGstin && (
                                    <div className="text-[11px] text-muted-foreground mt-2 font-mono">
                                        GSTIN: <span className="text-foreground/85">{clinicGstin}</span>
                                    </div>
                                )}
                            </BlockCard>

                            <BlockCard label="Bill to" testid="invoice-billto-block">
                                <div className="text-sm font-semibold text-foreground">
                                    {invoice.patient_name || "Patient"}
                                </div>
                                {invoice.patient_phone && (
                                    <div className="text-xs text-muted-foreground mt-1">{invoice.patient_phone}</div>
                                )}
                                {invoice.case_code && (
                                    <div className="text-[11px] text-muted-foreground mt-2 font-mono">
                                        Case · <span className="text-foreground/85">{invoice.case_code}</span>
                                    </div>
                                )}
                            </BlockCard>
                        </div>

                        {/* ─────────── LINE ITEMS ─────────── */}
                        <div className="rounded-xl border border-border overflow-hidden mb-8 bg-white">
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="bg-secondary/50 border-b border-border">
                                        <th className="text-left text-[10px] uppercase tracking-widest font-bold text-foreground/70 px-4 py-3">
                                            Description
                                        </th>
                                        <th className="text-right text-[10px] uppercase tracking-widest font-bold text-foreground/70 px-3 py-3 w-16">
                                            Qty
                                        </th>
                                        <th className="text-right text-[10px] uppercase tracking-widest font-bold text-foreground/70 px-3 py-3 w-32">
                                            Rate
                                        </th>
                                        <th className="text-right text-[10px] uppercase tracking-widest font-bold text-foreground/70 px-4 py-3 w-32">
                                            Amount
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(invoice.line_items || []).map((li, i) => (
                                        <tr
                                            key={i}
                                            className={`border-b border-border/40 last:border-0 ${i % 2 === 1 ? "bg-secondary/20" : ""}`}
                                            data-testid={`invoice-line-item-${i}`}
                                        >
                                            <td className="px-4 py-3 text-sm align-top">
                                                <div className="font-medium text-foreground">{li.description}</div>
                                                {li.line_type && li.line_type !== "service" && (
                                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                                                        {String(li.line_type).replace(/_/g, " ")}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-3 py-3 text-sm text-right tabular-nums align-top">{li.quantity}</td>
                                            <td className="px-3 py-3 text-sm text-right tabular-nums align-top">{formatINR(li.unit_price)}</td>
                                            <td className="px-4 py-3 text-sm text-right tabular-nums font-medium align-top">{formatINR(li.line_total)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* ─────────── TOTALS + UPI QR ─────────── */}
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-10">
                            {/* UPI QR (left side) — when available */}
                            <div className="md:col-span-7">
                                {showUpiQR ? (
                                    <div
                                        className="rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 p-5 flex items-center gap-5"
                                        data-testid="invoice-upi-block"
                                    >
                                        <div className="bg-white p-2 rounded-lg shrink-0 border border-border">
                                            <QRCodeSVG
                                                value={upiUri}
                                                size={112}
                                                level="M"
                                                includeMargin={false}
                                                data-testid="invoice-upi-qr"
                                            />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-primary">
                                                <QrCode size={11} weight="duotone" /> Scan to pay · UPI
                                            </div>
                                            <div className="font-display text-lg font-semibold mt-1 leading-tight">
                                                Pay {formatINR(invoice.balance)}
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-1">
                                                Open any UPI app — PhonePe, Google Pay, Paytm — and scan to pay {clinicName} directly.
                                            </div>
                                            <div className="text-[11px] font-mono text-foreground/80 mt-2">
                                                UPI ID: <span className="font-semibold">{doctorUpiId}</span>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    invoice.notes && (
                                        <div className="rounded-xl border border-border bg-secondary/30 p-4">
                                            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Notes</div>
                                            <div className="text-xs text-foreground/80 italic whitespace-pre-wrap leading-relaxed">
                                                {invoice.notes}
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>

                            {/* Totals (right side) */}
                            <div className="md:col-span-5">
                                <div className="rounded-xl border border-border bg-card p-5">
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between text-muted-foreground">
                                            <span>Subtotal</span>
                                            <span className="tabular-nums">{formatINR(invoice.subtotal)}</span>
                                        </div>
                                        {invoice.tax_amount > 0 && (
                                            <div className="flex justify-between text-muted-foreground">
                                                <span>{taxLabel}</span>
                                                <span className="tabular-nums">{formatINR(invoice.tax_amount)}</span>
                                            </div>
                                        )}
                                        <div className="border-t border-border pt-3 mt-3">
                                            <div className="flex justify-between font-display text-xl font-bold text-foreground">
                                                <span>Total</span>
                                                <span className="tabular-nums">{formatINR(invoice.total)}</span>
                                            </div>
                                        </div>
                                        {invoice.amount_paid > 0 && (
                                            <div className="flex justify-between text-emerald-700 pt-2">
                                                <span>Paid</span>
                                                <span className="tabular-nums">−{formatINR(invoice.amount_paid)}</span>
                                            </div>
                                        )}
                                        {invoice.amount_refunded > 0 && (
                                            <div className="flex justify-between text-violet-700">
                                                <span>Refunded</span>
                                                <span className="tabular-nums">+{formatINR(invoice.amount_refunded)}</span>
                                            </div>
                                        )}
                                        {(invoice.amount_paid > 0 || invoice.amount_refunded > 0) && (
                                            <div className="border-t border-border pt-2 mt-2">
                                                <div className={`flex justify-between font-semibold ${invoice.balance > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                                                    <span>Balance due</span>
                                                    <span className="tabular-nums">{formatINR(invoice.balance)}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ─────────── PAYMENTS RECORDED ─────────── */}
                        {(invoice.payments || []).length > 0 && (
                            <div className="mb-8" data-testid="invoice-payments-list">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">Payments recorded</div>
                                <div className="rounded-lg border border-border overflow-hidden">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="bg-secondary/40 border-b border-border">
                                                <th className="text-left font-semibold text-foreground/70 px-3 py-2">Date</th>
                                                <th className="text-left font-semibold text-foreground/70 px-3 py-2">Method</th>
                                                <th className="text-left font-semibold text-foreground/70 px-3 py-2">Reference</th>
                                                <th className="text-right font-semibold text-foreground/70 px-3 py-2">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoice.payments.map((p) => (
                                                <tr key={p.id} className="border-b border-border/40 last:border-0">
                                                    <td className="px-3 py-2">{new Date(p.paid_at).toLocaleDateString("en-IN")}</td>
                                                    <td className="px-3 py-2 capitalize">{(p.method || "cash").replace(/_/g, " ")}</td>
                                                    <td className="px-3 py-2 font-mono text-muted-foreground">{p.reference || "—"}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums font-medium text-emerald-700">{formatINR(p.amount)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* ─────────── NOTES (when not preempted by UPI block) ─────────── */}
                        {invoice.notes && showUpiQR && (
                            <div className="rounded-xl border-l-2 border-border bg-secondary/20 px-4 py-3 mb-8">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Notes</div>
                                <div className="text-xs text-foreground/80 italic whitespace-pre-wrap leading-relaxed">
                                    {invoice.notes}
                                </div>
                            </div>
                        )}

                        {/* ─────────── SIGNATURE + TERMS ─────────── */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 mt-12">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5">Terms</div>
                                <ul className="text-[11px] text-muted-foreground leading-relaxed space-y-1 list-disc list-inside">
                                    <li>Payable on or before the due date.</li>
                                    <li>This is a computer-generated tax invoice — no physical signature required.</li>
                                    {clinicGstin && <li>GST charged as per applicable rate.</li>}
                                </ul>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-3">Authorised signatory</div>
                                <div className="inline-block border-t border-foreground/30 pt-1 min-w-[180px]">
                                    <div className="text-sm font-semibold">{doctorName || clinicName}</div>
                                    {clinicName && doctorName && clinicName !== doctorName && (
                                        <div className="text-[11px] text-muted-foreground">{clinicName}</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ─────────── FOOTER ─────────── */}
                        <div className="border-t border-border pt-4 text-center" data-testid="invoice-footer">
                            <div className="text-[11px] text-muted-foreground whitespace-pre-wrap">
                                {billingFooter}
                            </div>
                            <div className="text-[9px] text-muted-foreground/70 mt-3 tracking-wider">
                                Invoice generated via Invisique™ · {new Date().toLocaleDateString("en-IN")}
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}


function BlockCard({ label, children, testid }) {
    return (
        <div
            className="rounded-xl border border-border bg-secondary/20 px-5 py-4"
            data-testid={testid}
        >
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
                {label}
            </div>
            {children}
        </div>
    );
}
